import { useOrganization } from '@clerk/react';
import cubejs from '@cubejs-client/core';
import { CubeProvider } from '@cubejs-client/react';
// @ts-expect-error js-cookie has no declaration file
import Cookies from 'js-cookie';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'src/auth';
import useExponentialBackoff from 'src/hooks/useExpontentialBackoff';

export const CUBE_TOKEN_COOKIE = 'cubeToken';
export const CUBE_TOKEN_EXPIRY_COOKIE = 'cubeToken-expiry';
// Identifies the organization the cached token was minted for, as reported by the server.
export const CUBE_TOKEN_ORG_COOKIE = 'cubeToken-org';

export const CubeTokenProvider = ({ children }: { children: ReactNode }) => {
  const [cubeToken, setCubeToken] = useState(Cookies.get(CUBE_TOKEN_COOKIE) || null);
  const [cubeTokenOrgId, setCubeTokenOrgId] = useState(Cookies.get(CUBE_TOKEN_ORG_COOKIE) || null);
  const { isAuthenticated } = useAuth();
  // Clerk reports no active organization until something calls setActive, and for a session with
  // several memberships that can stay null. This value is therefore a HINT used to notice an
  // organization change — never a precondition for having a token.
  const { organization } = useOrganization();
  const activeOrgId = organization?.id ?? null;
  const swapRequestedForRef = useRef<string | null>(null);
  const mintingRef = useRef(false);

  const fetchToken = useExponentialBackoff(async () => {
    if (!isAuthenticated) {
      return null;
    }

    const response = await fetch('/.redwood/functions/cubeToken', { method: 'GET' });
    const { token, expiry, organizationClerkId } = await response.json();

    if (!token) {
      return null;
    }

    // The server mints from the org on the verified session, which is the same context the API uses
    // for every other query — so it is the authority. Record what it says rather than asserting.
    setCubeToken(token);
    setCubeTokenOrgId(organizationClerkId ?? null);
    Cookies.set(CUBE_TOKEN_COOKIE, token, { expires: expiry });
    Cookies.set(CUBE_TOKEN_ORG_COOKIE, organizationClerkId ?? '', { expires: expiry });
    Cookies.set(CUBE_TOKEN_EXPIRY_COOKIE, DateTime.now().plus({ days: expiry }), { expires: expiry });

    return token;
  });

  // fetchToken is re-created every render and clearing the token to swap organizations re-triggers
  // the mount effect, so both paths funnel through mintingRef to stay one request.
  const mintOnce = useCallback(() => {
    if (mintingRef.current) {
      return;
    }

    mintingRef.current = true;
    fetchToken().finally(() => {
      mintingRef.current = false;
    });
  }, [fetchToken]);

  useEffect(() => {
    // Fetch the token when the component mounts.
    if (!cubeToken) {
      mintOnce();
    }
  }, [mintOnce, cubeToken]);

  useEffect(() => {
    // Cube derives row-level security from the org_id in this JWT (cube/cube.js queryRewrite), so a
    // token minted for a previously-active organization returns that organization's rows. Swap it
    // when we can see the organization changed — and only then, so a session whose active org is
    // simply unknown keeps working instead of losing Cube access entirely.
    if (!isAuthenticated || !activeOrgId || !cubeTokenOrgId || cubeTokenOrgId === activeOrgId) {
      return;
    }

    // One swap per organization: if the replacement still reports a different org (the session
    // cookie can lag Clerk's client state), keep the server's answer rather than looping or
    // blocking. Being consistent with the rest of the app beats showing nothing.
    if (swapRequestedForRef.current === activeOrgId) {
      return;
    }

    swapRequestedForRef.current = activeOrgId;
    setCubeToken(null);
    mintOnce();
  }, [mintOnce, isAuthenticated, activeOrgId, cubeTokenOrgId]);

  const cubejsApi = useMemo(() => cubejs(cubeToken ?? '', { apiUrl: process.env.CUBEJS_REST_API ?? '' }), [cubeToken]);

  useEffect(() => {
    // Check if the token is about to expire every time the app loads or token changes.
    const checkExpiry = async () => {
      const storedExpiry: string | undefined = Cookies.get(CUBE_TOKEN_EXPIRY_COOKIE);

      // No recorded expiry means there is nothing to pre-emptively refresh.
      if (!storedExpiry) {
        return;
      }

      const expiryDate = new Date(storedExpiry);
      const now = new Date();

      // Refresh token 20 min before expiry
      if (expiryDate.getTime() - now.getTime() <= 20 * 60 * 1000) {
        await fetchToken(); // Fetch a new token if it's expired
      }
    };

    checkExpiry();
  }, [fetchToken]);

  return <CubeProvider cubejsApi={cubejsApi}>{children}</CubeProvider>;
};
